import { ApolloClient } from "@apollo/client";
import { GET_PROMO_CODE_UID_IN_EVENT, GET_PROMO_CODE_USAGE } from "./queries";

// The customer types this word at the ticketing checkout, or an operator keys
// it in at the POS. Every lookup is an exact match on (event_id, uid), so the
// stored value is always upper case and the ticketing site upper-cases the
// typed value before it queries. Generated codes already use A-Z0-9 only.
export const CUSTOM_UID_PATTERN = /^[A-Z0-9]{4,32}$/;
export const CUSTOM_UID_SANITIZE = /[^A-Za-z0-9]/g;

export const normalizeCustomUid = (
  raw: FormDataEntryValue | null | undefined
): string => String(raw ?? "").trim().toUpperCase();

// Returns an error message for the operator, or null when the code can be used.
export const validateCustomUid = async (
  client: ApolloClient<any>,
  { uid, eventId, quantity }: { uid: string; eventId: number; quantity: number }
): Promise<string | null> => {
  if (!CUSTOM_UID_PATTERN.test(uid)) {
    return "Custom code must be 4 to 32 letters or numbers.";
  }
  if (quantity !== 1) {
    return "Set Quantity to 1 when you use a custom code.";
  }
  if (!eventId) {
    return "Select an event before you use a custom code.";
  }
  // Two rows with the same uid in one event would make the checkout lookup
  // return an arbitrary one, so refuse the duplicate here.
  const { data } = await client.query({
    query: GET_PROMO_CODE_UID_IN_EVENT,
    variables: { eventId, uid },
    fetchPolicy: "network-only",
  });
  if (data?.promo_code?.length) {
    return `Code ${uid} already exists for this event.`;
  }
  return null;
};

// True when any POS or ticketing order in the event already carries this code.
export const promoCodeHasOrders = async (
  client: ApolloClient<any>,
  { uid, eventId }: { uid: string; eventId: number }
): Promise<boolean> => {
  const { data } = await client.query({
    query: GET_PROMO_CODE_USAGE,
    variables: { eventId, uid },
    fetchPolicy: "network-only",
  });
  return Boolean(data?.orders?.length || data?.orders_ticketing?.length);
};

// Rename rules for the edit page. Orders and reports keep the code as text
// (orders.promo_code_uid, orders_ticketing.promo_code_uid), so a rename after
// use would split that code's history. Returns an error message or null.
export const validateUidRename = async (
  client: ApolloClient<any>,
  {
    currentUid,
    newUid,
    currentEventId,
    newEventId,
  }: {
    currentUid: string;
    newUid: string;
    currentEventId: number;
    newEventId: number;
  }
): Promise<string | null> => {
  const error = await validateCustomUid(client, {
    uid: newUid,
    eventId: newEventId,
    quantity: 1,
  });
  if (error) {
    return error;
  }
  if (await promoCodeHasOrders(client, { uid: currentUid, eventId: currentEventId })) {
    return `Code ${currentUid} has already been used by an order, so it cannot be renamed.`;
  }
  return null;
};
